// @flow

import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { graphql, QueryRenderer } from 'react-relay';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { i18n, getCurrencySymbol } from 'shared/utils';
import { colors, fonts } from 'shared/styleguide';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import * as Actions from 'main-app/store/Actions';
import BillingForm from 'main-app/components/BillingForm';
import SubscribeWithMethod from 'main-app/mutations/SubscribeWithMethod';
import UpdateBillingPayment from 'main-app/mutations/UpdateBillingPayment';
import { isStripeConfigured } from 'shared/utils/stripe';

/* ==================== Styled Components ==================== */

const Content = styled.div`
  padding: 24px;
  max-width: 900px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px 0;
`;

const Card = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.lightBlueGrey};
  border-radius: 5px;
  padding: 24px;
  margin-bottom: 24px;
`;

const CardTitle = styled.div`
  ${fonts.headerBold};
  color: ${colors.charcoalGrey};
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${colors.paleGrey};

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  ${fonts.bodyRegular};
  color: ${colors.battleshipGrey};
`;

const Value = styled.span`
  ${fonts.bodyBold};
  color: ${colors.charcoalGrey};
`;

const StatusBadge = styled.span`
  ${fonts.smallBold};
  padding: 4px 10px;
  border-radius: 12px;
  text-transform: uppercase;
  color: ${colors.white};
  background: ${(p) => {
    switch (p.status) {
      case 'PLAN_ACTIVE':
        return colors.greenBlue;
      case 'TRIAL_ACTIVE':
        return colors.macaroniAndCheese;
      case 'PAYMENT_ISSUE':
        return colors.cherry;
      case 'TRIAL_EXPIRED_NO_PAYMENT':
        return colors.cherry;
      default:
        return colors.coolGrey;
    }
  }};
`;

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    ${fonts.smallBold};
    color: ${colors.battleshipGrey};
    text-transform: uppercase;
    text-align: left;
    padding: 8px 12px;
    border-bottom: 2px solid ${colors.lightBlueGrey};
  }

  td {
    ${fonts.bodyRegular};
    color: ${colors.charcoalGrey};
    padding: 10px 12px;
    border-bottom: 1px solid ${colors.paleGrey};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const EmptyState = styled.div`
  ${fonts.bodyRegular};
  color: ${colors.coolGrey};
  text-align: center;
  padding: 40px;
`;

const PaidBadge = styled.span`
  ${fonts.smallBold};
  padding: 2px 8px;
  border-radius: 10px;
  color: ${colors.white};
  background: ${(p) => (p.paid ? colors.greenBlue : colors.salmon)};
`;

/* ==================== Helper Functions ==================== */

function formatStatus(status: string): string {
  switch (status) {
    case 'PLAN_ACTIVE':
      return 'Active';
    case 'TRIAL_ACTIVE':
      return 'Trial';
    case 'PAYMENT_ISSUE':
      return 'Payment Issue';
    case 'TRIAL_EXPIRED_NO_PAYMENT':
      return 'Trial Expired';
    default:
      return status;
  }
}

function formatDate(dateString: string): string {
  if (!dateString) return '—';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount: ?number): string {
  if (amount == null) return `${getCurrencySymbol()}0.00`;
  return `${getCurrencySymbol()}${amount.toFixed(2)}`;
}

/* ==================== Component ==================== */

type State = {
  subscribeModalOpen: boolean,
  updateCardModalOpen: boolean,
};

class Billing extends PureComponent<Object, State> {
  state = {
    subscribeModalOpen: false,
    updateCardModalOpen: false,
  };

  handleSubscribe = async (token: string, values: Object) => {
    await SubscribeWithMethod.commit({
      variables: {
        input: {
          stripeToken: token,
          userLicenses: parseInt(values.userLicenses, 10) || 1,
        },
      },
    });
    Actions.alertNotification('Subscription activated successfully!', 'success');
  };

  handleUpdateCard = async (token: string) => {
    await UpdateBillingPayment.commit({
      variables: {
        input: { stripeToken: token },
      },
    });
    Actions.alertNotification('Payment method updated successfully!', 'success');
  };

  render() {
    const { subscribeModalOpen, updateCardModalOpen } = this.state;

    if (!isStripeConfigured()) {
      return (
        <Content>
          <EmptyState>
            Billing is not configured for this environment.
          </EmptyState>
        </Content>
      );
    }

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query BillingQuery {
            billingDetails {
              id
              status
              productName
              planName
              planDescription
              planInterval
              planCostUSD
              totalSeats
              active
              trialActive
              trialDaysRemaining
              fullName
              cardType
              cardLast4
              cardExpMonth
              cardExpYear
              cardAddressStatus
              cardZipStatus
              cardCVCStatus
              addressLine1
              addressLine2
              city
              state
              zip
              country
            }
            billingInvoices {
              id
              paid
              periodStart
              periodEnd
              amountUSD
              amountPaidUSD
              amountOutstandingUSD
              attempts
              invoicePDFUrl
              invoiceUrl
            }
          }
        `}
        render={({ props }) => {
          if (!props) {
            return <Loader />;
          }

          const billing = props.billingDetails;
          const invoices = props.billingInvoices || [];
          const hasSubscription = Boolean(billing);
          const needsSubscription = !hasSubscription;

          return (
            <>
              <ActionBar>
                {hasSubscription ? (
                  <Button
                    onClick={() => this.setState({ updateCardModalOpen: true })}
                  >
                    {i18n.t('Update Card')}
                  </Button>
                ) : (
                  <Button
                    theme="green"
                    onClick={() => this.setState({ subscribeModalOpen: true })}
                  >
                    {i18n.t('Subscribe')}
                  </Button>
                )}
              </ActionBar>
              <Content>
                {/* Plan Status Card */}
                <Card>
                  <CardTitle>{i18n.t('Subscription')}</CardTitle>
                  {!hasSubscription ? (
                    <EmptyState>
                      No active subscription. Click Subscribe to get started.
                    </EmptyState>
                  ) : (
                    <>
                      <Row>
                        <Label>{i18n.t('Status')}</Label>
                        <StatusBadge status={billing.status}>
                          {formatStatus(billing.status)}
                        </StatusBadge>
                      </Row>
                      <Row>
                        <Label>{i18n.t('Plan')}</Label>
                        <Value>{billing.planDescription}</Value>
                      </Row>
                      <Row>
                        <Label>{i18n.t('Product')}</Label>
                        <Value>{billing.productName}</Value>
                      </Row>
                      <Row>
                        <Label>{i18n.t('Total Seats')}</Label>
                        <Value>{billing.totalSeats}</Value>
                      </Row>
                      {billing.trialActive && (
                        <Row>
                          <Label>{i18n.t('Trial Days Remaining')}</Label>
                          <Value>{billing.trialDaysRemaining}</Value>
                        </Row>
                      )}
                    </>
                  )}
                </Card>

                {/* Payment Method Card */}
                {hasSubscription && (
                  <Card>
                    <CardTitle>{i18n.t('Payment Method')}</CardTitle>
                    {billing.cardLast4 ? (
                      <>
                        <Row>
                          <Label>{i18n.t('Card')}</Label>
                          <Value>
                            {(billing.cardType || '').toUpperCase()} ****{' '}
                            {billing.cardLast4}
                          </Value>
                        </Row>
                        <Row>
                          <Label>{i18n.t('Expires')}</Label>
                          <Value>
                            {billing.cardExpMonth}/{billing.cardExpYear}
                          </Value>
                        </Row>
                        <Row>
                          <Label>{i18n.t('Name')}</Label>
                          <Value>{billing.fullName}</Value>
                        </Row>
                        {billing.addressLine1 ? (
                          <Row>
                            <Label>{i18n.t('Billing Address')}</Label>
                            <Value>
                              {billing.addressLine1}
                              {billing.addressLine2 ? `, ${billing.addressLine2}` : ''}
                              {`, ${billing.city}, ${billing.state} ${billing.zip}`}
                            </Value>
                          </Row>
                        ) : null}
                      </>
                    ) : (
                      <EmptyState>
                        No payment method on file.
                      </EmptyState>
                    )}
                  </Card>
                )}

                {/* Invoice History */}
                <Card>
                  <CardTitle>{i18n.t('Invoice History')}</CardTitle>
                  {invoices.length === 0 ? (
                    <EmptyState>{i18n.t('No invoices yet.')}</EmptyState>
                  ) : (
                    <InvoiceTable>
                      <thead>
                        <tr>
                          <th>{i18n.t('Period')}</th>
                          <th>{i18n.t('Amount')}</th>
                          <th>{i18n.t('Status')}</th>
                          <th>{i18n.t('PDF')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.id}>
                            <td>
                              {formatDate(inv.periodStart)} &mdash;{' '}
                              {formatDate(inv.periodEnd)}
                            </td>
                            <td>{formatCurrency(inv.amountUSD)}</td>
                            <td>
                              <PaidBadge paid={inv.paid}>
                                {inv.paid ? 'Paid' : 'Unpaid'}
                              </PaidBadge>
                            </td>
                            <td>
                              {inv.invoicePDFUrl ? (
                                <a
                                  href={inv.invoicePDFUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {i18n.t('Download')}
                                </a>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </InvoiceTable>
                  )}
                </Card>
              </Content>

              {/* Subscribe Modal */}
              {subscribeModalOpen && (
                <BillingForm
                  title="Subscribe"
                  submitLabel="Subscribe & Pay"
                  showLicenseField
                  onClose={() => this.setState({ subscribeModalOpen: false })}
                  onSubmit={this.handleSubscribe}
                />
              )}

              {/* Update Card Modal */}
              {updateCardModalOpen && (
                <BillingForm
                  title="Update Payment Method"
                  submitLabel="Update Card"
                  onClose={() => this.setState({ updateCardModalOpen: false })}
                  onSubmit={this.handleUpdateCard}
                  initialValues={
                    billing
                      ? {
                          fullName: billing.fullName,
                          addressLine1: billing.addressLine1,
                          addressLine2: billing.addressLine2,
                          city: billing.city,
                          state: billing.state,
                          zip: billing.zip,
                          country: billing.country,
                        }
                      : undefined
                  }
                />
              )}
            </>
          );
        }}
      />
    );
  }
}

export default Billing;
